/**
 * Ambient floating launcher for the unified assistant: a draggable circular
 * "puck" showing the Cofactor mark, pinned to a screen corner (default
 * bottom-right) and snapping to the nearest corner with momentum on release.
 * Toggles the floating `AssistantPanel` on click — dragging never toggles.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { TooltipTrigger, Tooltip } from '@chatsdet/ui';
import { EXCLUDE_ATTR } from '@runcofactor/guide';
import { PUCK_SIZE, TAKEOVER_PULSE_MAGNITUDE, type Corner, type Point } from './cornerPin';
import { LogoMarkPhysics } from './LogoMarkPhysics';
import { useCornerPin } from './useCornerPin';

export type AssistantLauncherHandle = {
  /** Fly the puck to an arbitrary top-left point (captive-takeover flight). */
  flyTo: (point: Point, opts?: { windUp?: boolean; landingPopMagnitude?: number }) => Promise<void>;
  /** Fly the puck back to its pinned corner. */
  returnToCorner: () => Promise<void>;
  /** Fire a disc pop directly — the takeover's celebratory reappearance pulse. */
  pulse: (magnitude: number) => void;
};

export type AssistantLauncherProps = {
  corner: Corner;
  onCornerChange: (corner: Corner) => void;
  isOpen: boolean;
  onToggle: () => void;
  /**
   * True while the captive-takeover surface owns the puck — dragging is
   * disabled and the puck itself vanishes instantly (the surface's own
   * center-mark morph takes over — see `AssistantPanel.tsx`'s `takeover.mark`
   * — rather than the puck fading itself), reappearing with a soft fade once
   * the takeover starts returning.
   */
  hidden?: boolean;
  /**
   * Overrides the click handler while the puck is mid-flight (takeover
   * `flying`) — a click there cancels the takeover instead of toggling the
   * ordinary panel.
   */
  onClickOverride?: () => void;
};

export const AssistantLauncher = React.forwardRef<AssistantLauncherHandle, AssistantLauncherProps>(
  function AssistantLauncher(
    { corner, onCornerChange, isOpen, onToggle, hidden = false, onClickOverride },
    ref
  ) {
    const reducedMotion = useReducedMotion();
    const { x, y, dragProps, didDragRef, accelRef, activeRef, impulseRef, runToken, pulse, flyTo, returnToCorner } =
      useCornerPin(corner, onCornerChange);

    React.useImperativeHandle(ref, () => ({ flyTo, returnToCorner, pulse }), [flyTo, returnToCorner, pulse]);

    const handleClick = React.useCallback(() => {
      // A drag ends with a synthetic click; swallow exactly that one, then
      // re-arm so ordinary clicks keep working without another drag.
      if (didDragRef.current) {
        didDragRef.current = false;
        return;
      }
      if (onClickOverride) {
        onClickOverride();
        return;
      }
      onToggle();
    }, [didDragRef, onClickOverride, onToggle]);

    // Pop the discs apart on open, collapse them together on dismiss. Keyed on
    // the `isOpen` transition (not the click handler) so it fires the same way
    // whether the panel closed via the puck, the panel's X button, or the
    // global Cmd/Ctrl+. shortcut. `prevOpenRef` starts at the first-render
    // value so a page load with the panel already open (persisted in user
    // settings) never fires the effect — only an actual open/close does.
    const prevOpenRef = React.useRef(isOpen);
    React.useEffect(() => {
      if (prevOpenRef.current !== isOpen) {
        pulse(isOpen ? TAKEOVER_PULSE_MAGNITUDE : -TAKEOVER_PULSE_MAGNITUDE);
      }
      prevOpenRef.current = isOpen;
    }, [isOpen, pulse]);

    const launcher = (
      <motion.div
        drag={!hidden}
        dragMomentum={false}
        dragElastic={0}
        // `opacity: 0` alone leaves the element hit-testable — while hidden
        // (opacity 0, sitting at the content-area center during a takeover)
        // it must not intercept hover/click at all, or its tooltip floats
        // over the conversation and a click there fights the surface
        // underneath it.
        style={{ x, y, width: PUCK_SIZE, height: PUCK_SIZE, pointerEvents: hidden ? 'none' : 'auto' }}
        className="fixed left-0 top-0 z-50 cursor-grab active:cursor-grabbing"
        // The launcher toggles the assistant closed on click — off-limits to
        // the guide for the same reason the panel itself is.
        {...{ [EXCLUDE_ATTR]: true }}
        {...dragProps}
        // The takeover surface's own center mark (`AssistantPanel.tsx`'s
        // `takeover.mark`) takes over the morph the instant expansion starts,
        // so the puck itself vanishes AT t=0 (no fade — a fade here would
        // double up with the mark's own fade and read as two objects, not
        // one). Reappearing is softer: a quick fade back in once returning.
        animate={{ opacity: hidden ? 0 : 1 }}
        transition={{ duration: reducedMotion ? 0 : hidden ? 0 : 0.15 }}
      >
        <TooltipTrigger>
          <button
            type="button"
            onClick={handleClick}
            aria-label="Assistant"
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            aria-hidden={hidden}
            tabIndex={hidden ? -1 : 0}
            disabled={hidden}
            className="flex h-full w-full items-center justify-center overflow-hidden rounded-full border border-border bg-background shadow-lg transition-shadow hover:shadow-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <LogoMarkPhysics
              size={44}
              accelRef={accelRef}
              activeRef={activeRef}
              impulseRef={impulseRef}
              runToken={runToken}
            />
          </button>
          <Tooltip>Assistant · ⌘.</Tooltip>
        </TooltipTrigger>
      </motion.div>
    );

    return createPortal(launcher, document.body);
  }
);
